import { add, money, toDb, ZERO, type Money } from '@miraki/money';
import type { ReturnDecision, ReturnLine, ReturnReceipt, ReturnRequestView } from '@miraki/contracts';
import { ApiError } from '../api/client';

/**
 * A-43 Returns — what the screen OFFERS and what it SENDS, kept pure and tested. The rules are the
 * server's (`returns/admin-rules.ts`) and it checks them again; this only stops the screen asking
 * for something it can already see is wrong, and says why in words.
 *
 * Two rules shape everything here:
 *   - D71: a decision is PER PIECE. Each requested piece is approved, refused or left for later,
 *     individually; there is no "approve all", because a button that covers every piece hides
 *     which pieces it covered. A refusal carries its own reason.
 *   - CN-14: a receipt releases BUDGET, never money. The one sum this module does is the value of
 *     the pieces Admin has ticked as arrived — what a person is choosing, shown before they press —
 *     and it is labelled as budget released, never as a credit.
 */

export { LINE_TONE } from './shop/returns-view';

export type Choice = 'APPROVE' | 'REFUSE';
export interface DraftEntry { readonly choice: Choice; readonly reason: string }
/** Admin's draft decision: one entry per piece they have chosen something for. */
export type Draft = Readonly<Record<string, DraftEntry>>;

export const REASON_MIN = 3;

/** Where a request stands, for the queue's chip. Read from counts — there is no stored status. */
export type Stage = 'DECIDE' | 'GOODS' | 'DONE';
export function stage(v: Pick<ReturnRequestView, 'withdrawnAt' | 'counts'>): Stage {
  if (v.withdrawnAt === null && v.counts.REQUESTED > 0) return 'DECIDE';
  if (v.counts.APPROVED > 0) return 'GOODS';
  return 'DONE';
}

export const decidable = (l: Pick<ReturnLine, 'state'>) => l.state === 'REQUESTED';
export const receivable = (l: Pick<ReturnLine, 'state'>) => l.state === 'APPROVED';

/**
 * The decision to send, or why not yet. Only pieces Admin chose are sent; the rest stay REQUESTED
 * for another sitting (D71 allows deciding across sittings). A refusal with a reason under three
 * characters is held back here, naming the pieces, rather than relayed as a 409.
 */
export function decisionPayload(lines: readonly ReturnLine[], draft: Draft):
  { ok: true; payload: ReturnDecision } | { ok: false; reason: 'NOTHING_CHOSEN' } | { ok: false; reason: 'REASON_MISSING'; pieceIds: string[] } {
  const chosen = lines.filter((l) => decidable(l) && draft[l.pieceId]);
  if (chosen.length === 0) return { ok: false, reason: 'NOTHING_CHOSEN' };
  const missing = chosen.filter((l) => draft[l.pieceId]!.choice === 'REFUSE' && draft[l.pieceId]!.reason.trim().length < REASON_MIN);
  if (missing.length > 0) return { ok: false, reason: 'REASON_MISSING', pieceIds: missing.map((l) => l.pieceId) };
  return {
    ok: true,
    payload: {
      lines: chosen.map((l) => {
        const p = draft[l.pieceId]!;
        return p.choice === 'APPROVE'
          ? { pieceId: l.pieceId, decision: 'APPROVE' as const }
          : { pieceId: l.pieceId, decision: 'REFUSE' as const, reason: p.reason.trim() };
      }),
    },
  };
}

/** How many of each the draft would record — the button says it, so nobody approves blind. */
export function draftCounts(lines: readonly ReturnLine[], draft: Draft): { approve: number; refuse: number } {
  const chosen = lines.filter((l) => decidable(l)).map((l) => draft[l.pieceId]?.choice);
  return { approve: chosen.filter((c) => c === 'APPROVE').length, refuse: chosen.filter((c) => c === 'REFUSE').length };
}

/** The receipt to send: only approved pieces, and only those ticked. */
export function receiptPayload(lines: readonly ReturnLine[], ticked: ReadonlySet<string>, receivedOn: string): ReturnReceipt | null {
  const pieceIds = lines.filter((l) => receivable(l) && ticked.has(l.pieceId)).map((l) => l.pieceId);
  if (pieceIds.length === 0) return null;
  return receivedOn ? { pieceIds, receivedOn } : { pieceIds };
}

/** CN-14 — the budget the ticked pieces will release, per currency (never summed across, AN-08). */
export function releasing(lines: readonly ReturnLine[], ticked: ReadonlySet<string>): Array<{ currency: string; amount: string }> {
  const by = new Map<string, Money>();
  for (const l of lines) if (receivable(l) && ticked.has(l.pieceId)) by.set(l.currency, add(by.get(l.currency) ?? ZERO, money(l.wholesaleValue)));
  return [...by.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, m]) => ({ currency, amount: toDb(m) }));
}

type Refusal = { code: string; pieceId?: string; status?: string; state?: string; receivedOn?: string; today?: string; requestedOn?: string };

/**
 * A 409 from either write lists EVERY refusal. One line each, the piece named by Job Number — a
 * piece id means nothing to the person looking at the box. Null for any other error, so it is
 * shown as the error it is rather than dressed up as a refusal.
 */
export function refusalLines(err: unknown, jobOf: (pieceId: string) => string | undefined):
  Array<{ key: string; values: Record<string, string> }> | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.detail as { code?: unknown; refusals?: unknown } | undefined;
  if (!['RETURN_DECISION_REFUSED', 'RETURN_RECEIPT_REFUSED', 'RECALL_CANCEL_REFUSED'].includes(String(d?.code)) || !Array.isArray(d?.refusals)) return null;
  return (d.refusals as Refusal[]).map((r) => ({
    key: `returnsDesk.refusal.${r.code}`,
    values: {
      job: (r.pieceId && jobOf(r.pieceId)) ?? r.pieceId ?? '', status: r.status ?? '', state: r.state ?? '',
      receivedOn: r.receivedOn ?? '', today: r.today ?? '', requestedOn: r.requestedOn ?? '',
    },
  }));
}
